import React, { useMemo, useState } from 'react';
import { Alert, App, Button, Card, Col, Descriptions, Dropdown, Row, Space, Spin, Table, Tag, Typography } from 'antd';
import {
  ArrowLeftOutlined,
  EditOutlined,
  FilePdfOutlined,
  MoreOutlined,
  SendOutlined,
  UserAddOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { EnterprisePageHeader, WorkflowStrip, WorkspaceLayout } from '@/components/enterprise';
import { ApprovalPanel } from '@/components/approval/ApprovalPanel';
import EntityAttachmentsCard from '@/components/documents/EntityAttachmentsCard';
import { ENTITY_TYPE_HR_OFFER } from '@/constants/approval-entity-types';
import hrmsService, { hrmsKeys } from '@/services/hrms.service';
import { buildLinearWorkflow, hrmsStatusColor, showApprovalPanel } from '../shared/hrmsStatus';
import ReasonPrompt from './shared/ReasonPrompt';
import { fmtDate, fmtDateTime, fmtMoney, parseJsonArray, recruitErrorMessage, stageLabel, useRecruitAccess } from './shared/recruitUi';

const { Text, Paragraph } = Typography;

const OfferDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const { canView, canManage, canConvert } = useRecruitAccess();
  const [loading, setLoading] = useState<string | null>(null);
  const [prompt, setPrompt] = useState<null | 'withdraw'>(null);

  const { data: row, isLoading } = useQuery({
    queryKey: hrmsKeys.offer(id || ''),
    queryFn: () => hrmsService.getOffer(id!),
    enabled: Boolean(id),
  });

  const workflow = useMemo(
    () =>
      buildLinearWorkflow(['Draft', 'PendingApproval', 'Sent', 'Accepted', 'Converted'], row?.status || 'Draft', {
        rejectedStatuses: ['Rejected', 'Declined', 'Withdrawn', 'Expired'],
      }),
    [row?.status]
  );

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: hrmsKeys.offer(id!) });
    await queryClient.invalidateQueries({ queryKey: hrmsKeys.offers() });
    if (row?.application_id) await queryClient.invalidateQueries({ queryKey: hrmsKeys.application(row.application_id) });
  };

  const run = async (key: string, fn: () => Promise<any>, okMsg: string) => {
    setLoading(key);
    try {
      await fn();
      message.success(okMsg);
      await invalidate();
    } catch (e: any) {
      message.error(recruitErrorMessage(e));
    } finally {
      setLoading(null);
    }
  };

  const downloadPdf = async () => {
    setLoading('pdf');
    try {
      const blob = await hrmsService.getOfferPdf(id!);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${row.offer_number}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      message.error(recruitErrorMessage(e, 'Could not generate the offer letter'));
    } finally {
      setLoading(null);
    }
  };

  if (isLoading) {
    return (
      <WorkspaceLayout header={<EnterprisePageHeader documentLabel="Offer" />}>
        <Spin />
      </WorkspaceLayout>
    );
  }
  if (!row) {
    return (
      <WorkspaceLayout header={<EnterprisePageHeader documentLabel="Offer" />}>
        <Card>Not found</Card>
      </WorkspaceLayout>
    );
  }

  const allowances = parseJsonArray<{ name: string; amount: number }>(row.allowances_json);
  const gross = Number(row.basic_salary || 0) + allowances.reduce((s, a) => s + Number(a.amount || 0), 0);
  const money = (n: number) => fmtMoney(n || 0, row.currency);
  const editable = ['Draft', 'Rejected'].includes(row.status);
  const withdrawable = ['Draft', 'PendingApproval', 'Sent', 'Accepted'].includes(row.status);
  const expiresSoon = row.status === 'Sent' && row.offer_expires_at && dayjs(row.offer_expires_at).diff(dayjs(), 'day') <= 2;
  const contingencies = [
    row.contingency_medical && 'Medical fitness',
    row.contingency_visa && 'Visa / work permit',
    row.contingency_background && 'Background and reference checks',
  ].filter(Boolean) as string[];

  const moreItems = [
    ...(row.status === 'Sent' && canManage ? [{ key: 'resend', label: 'Resend email to candidate' }] : []),
    ...(withdrawable && canManage ? [{ key: 'withdraw', label: 'Withdraw offer', danger: true }] : []),
  ];

  return (
    <WorkspaceLayout
      fullBleed
      header={
        <EnterprisePageHeader
          documentLabel="Offer"
          documentNumber={row.offer_number}
          subtitle={
            <Text type="secondary">
              {canView ? <a onClick={() => navigate(`/hr/candidates/${row.candidate_id}`)}>{row.candidate_name}</a> : row.candidate_name} ·{' '}
              {row.job_title}
            </Text>
          }
          statusTag={<Tag color={hrmsStatusColor(row.status)}>{stageLabel(row.status)}</Tag>}
          primaryActions={
            <Space wrap>
              <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(canView ? '/hr/offers' : '/home/approvals')}>
                Back
              </Button>
              <Button icon={<FilePdfOutlined />} loading={loading === 'pdf'} onClick={downloadPdf}>
                Offer letter
              </Button>
              {editable && canManage && (
                <Button icon={<EditOutlined />} onClick={() => navigate(`/hr/offers/${id}/edit`)}>
                  Edit
                </Button>
              )}
              {editable && canManage && (
                <Button type="primary" icon={<SendOutlined />} loading={loading === 'submit'} onClick={() => run('submit', () => hrmsService.submitOffer(id!), 'Offer submitted')}>
                  Submit
                </Button>
              )}
              {row.status === 'Accepted' && canConvert && (
                <Button type="primary" icon={<UserAddOutlined />} onClick={() => navigate(`/hr/offers/${id}/convert`)}>
                  Convert to employee
                </Button>
              )}
              {moreItems.length > 0 && (
                <Dropdown
                  menu={{
                    items: moreItems,
                    onClick: ({ key }) => {
                      if (key === 'resend') void run('resend', () => hrmsService.resendOffer(id!), 'Offer email re-sent');
                      if (key === 'withdraw') setPrompt('withdraw');
                    },
                  }}
                >
                  <Button icon={<MoreOutlined />} />
                </Dropdown>
              )}
            </Space>
          }
        />
      }
      workflow={<WorkflowStrip stages={workflow.stages} current={workflow.current} compact />}
    >
      {expiresSoon && <Alert type="warning" showIcon style={{ marginTop: 16 }} message={`Offer expires ${fmtDateTime(row.offer_expires_at)} — follow up with the candidate or resend.`} />}
      {row.status === 'Converted' && row.converted_employee_id && (
        <Alert
          type="success"
          showIcon
          style={{ marginTop: 16 }}
          message={
            <span>
              Hired as <a onClick={() => navigate(`/hr/employees/${row.converted_employee_id}`)}>{row.converted_employee_code} — {row.converted_employee_name}</a>
            </span>
          }
        />
      )}
      <Row gutter={16} style={{ marginTop: 16 }}>
        <Col xs={24} lg={16}>
          <Card size="small" title="Package" style={{ marginBottom: 16 }}>
            <Descriptions column={2} size="small">
              <Descriptions.Item label="Basic">{money(row.basic_salary)}</Descriptions.Item>
              <Descriptions.Item label="Gross monthly">
                <Text strong>{money(gross)}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Join date">{fmtDate(row.join_date)}</Descriptions.Item>
              <Descriptions.Item label="Probation">{row.probation_months ?? 0} months</Descriptions.Item>
              <Descriptions.Item label="Employment">{row.employment_type || '—'}</Descriptions.Item>
              <Descriptions.Item label="Contract">{row.contract_type || '—'}</Descriptions.Item>
              <Descriptions.Item label="Department">{row.department_name || '—'}</Descriptions.Item>
              <Descriptions.Item label="Application">
                {canView ? (
                  <a onClick={() => navigate(`/hr/applications/${row.application_id}`)}>{row.application_number}</a>
                ) : (
                  row.application_number
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Conditions" span={2}>
                {contingencies.length ? contingencies.map((c) => <Tag key={c}>{c}</Tag>) : 'None'}
              </Descriptions.Item>
            </Descriptions>
            {allowances.length > 0 && (
              <Table
                style={{ marginTop: 12 }}
                rowKey="name"
                size="small"
                pagination={false}
                dataSource={allowances}
                columns={[
                  { title: 'Allowance', dataIndex: 'name' },
                  { title: 'Monthly', dataIndex: 'amount', align: 'right', render: (v) => money(v) },
                ]}
              />
            )}
          </Card>
          {(row.candidate_responded_at || row.withdrawn_reason || row.rejection_reason_code || row.offer_expires_at) && (
            <Card size="small" title="History" style={{ marginBottom: 16 }}>
              <Descriptions column={1} size="small">
                {row.offer_expires_at && <Descriptions.Item label="Valid until">{fmtDateTime(row.offer_expires_at)}</Descriptions.Item>}
                {row.candidate_responded_at && (
                  <Descriptions.Item label="Candidate responded">
                    {fmtDateTime(row.candidate_responded_at)}
                    {row.candidate_response_notes ? ` — ${row.candidate_response_notes}` : ''}
                  </Descriptions.Item>
                )}
                {row.rejection_reason_code && <Descriptions.Item label="Approval rejected">{row.rejection_reason_code}</Descriptions.Item>}
                {row.withdrawn_reason && <Descriptions.Item label="Withdrawn">{row.withdrawn_reason}</Descriptions.Item>}
              </Descriptions>
            </Card>
          )}
          {row.portal_url && (
            <Card size="small" title="Candidate portal" style={{ marginBottom: 16 }}>
              <Paragraph copyable={{ text: row.portal_url }} style={{ marginBottom: 4 }}>
                <Text code>{row.portal_url}</Text>
              </Paragraph>
              <Text type="secondary">The candidate can view, download, accept or decline the offer here. The link was emailed with the PDF attached.</Text>
            </Card>
          )}
          {row.notes && (
            <Card size="small" title="Internal notes" style={{ marginBottom: 16 }}>
              <Paragraph style={{ whiteSpace: 'pre-wrap', marginBottom: 0 }}>{row.notes}</Paragraph>
            </Card>
          )}
          <EntityAttachmentsCard entityType="HR_OFFER" entityId={row.id} title="Offer documents" uploadHint="Signed offer, compensation worksheet, contingency clearances" />
        </Col>
        <Col xs={24} lg={8}>
          {showApprovalPanel(row) && (
            <ApprovalPanel entityType={ENTITY_TYPE_HR_OFFER} entityId={row.id} extraInvalidateQueryKeys={[hrmsKeys.offer(id!), hrmsKeys.offers()]} />
          )}
        </Col>
      </Row>

      <ReasonPrompt
        open={prompt === 'withdraw'}
        title="Withdraw offer"
        okText="Withdraw"
        notesRequired
        notesLabel="Reason"
        onCancel={() => setPrompt(null)}
        onSubmit={async (v) => {
          setPrompt(null);
          await run('withdraw', () => hrmsService.withdrawOffer(id!, v.notes || ''), 'Offer withdrawn');
        }}
      />
    </WorkspaceLayout>
  );
};

export default OfferDetailPage;
